import { Cartographic } from "../math";
import { Vector3 } from "three";
import { assert } from "../utils";
/**
 *
 */
export class EllipsoidTessellator {
    constructor(tilingScheme) {
        this.tilingScheme = tilingScheme;
    }
    /**
     * Will tessellate a patch of a spheroid based on the provided aspect ratio, number of subdivisions, and a set of
     * bounds in longitude/latitude.
     *
     * @param rectangle The cartographic region to produce a geometry patch for
     * @param numSubdivisions A vector that encodes the number of subdivisions to use along the longitudinal and
     *                     latitudinal axis in the x and y components respectively.
     */
    tessellateCartographicSection(rectangle, numSubdivisions) {
        const subdivisionsX = Math.trunc(numSubdivisions.x);
        const subdivisionsY = Math.trunc(numSubdivisions.y);
        const vertexCount = EllipsoidTessellator.vertexCountFor(subdivisionsX, subdivisionsY);
        const indexCount = EllipsoidTessellator.indexCountFor(subdivisionsX, subdivisionsY);
        const positions = Array.from({ length: vertexCount * 3 }, () => 0);
        const normals = Array.from({ length: vertexCount * 3 }, () => 0);
        const uvs = Array.from({ length: vertexCount * 2 }, () => 0);
        const indices = Array.from({ length: indexCount }, () => 0);
        assert(subdivisionsX >= 2, "subdivisions.x >= 2");
        assert(subdivisionsY >= 2, "subdivisions.y >= 2");
        assert(positions.length >= vertexCount * 3, "positions.length >= vertexCount * 3");
        assert(uvs.length >= vertexCount * 2, "uvs.length >= vertexCount * 2");
        assert(indices.length >= indexCount, "indices.length >= indexCount");
        const uvMin = this.tilingScheme.projection.project(rectangle.southwest).setZ(0);
        const uvMax = this.tilingScheme.projection.project(rectangle.northeast).setZ(1);
        const uvInterval = uvMax.sub(uvMin);
        // Calculate the angular step sizes we need to take between arc strips
        const latitudeStep = rectangle.height / (subdivisionsY - 1);
        const longitudeStep = rectangle.width / (subdivisionsX - 1);
        // This will be used for calculating the midpoint of all the vertices
        const midpoint = new Vector3(0, 0, 0);
        // This will be used to calculate the upper bound of an AABB that contains all vertices in the patch
        const maxPos = new Vector3(Number.MIN_VALUE, Number.MIN_VALUE, Number.MIN_VALUE);
        // This will be used to calculate the lower bound of an AABB that contains all vertices in the patch
        const minPos = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
        // Calculate the actual vertex data
        const scratchCartographic = new Cartographic();
        const scratchPos = new Vector3();
        const scratchNrm = new Vector3();
        const scratchUv = new Vector3();
        for (let i = 0; i < subdivisionsY; i += 1) {
            scratchCartographic.setLatitude(rectangle.south + latitudeStep * i);
            for (let j = 0; j < subdivisionsX; j += 1) {
                scratchCartographic.setLongitude(rectangle.west + longitudeStep * j);
                const posOutOffset = j * subdivisionsY * 3;
                const uvOutOffset = j * subdivisionsY * 2;
                // Calculate the vertex positions in world space
                this.tilingScheme.ellipsoid.cartesianForCartographic(scratchCartographic, scratchPos);
                // Store the vertex positions into the buffer
                positions[i * 3 + posOutOffset] = scratchPos.x;
                positions[i * 3 + 1 + posOutOffset] = scratchPos.y;
                positions[i * 3 + 2 + posOutOffset] = scratchPos.z;
                // Add to the position accumulator, which we use to calculate the average (midpoint) after this loop is
                // done
                midpoint.x += scratchPos.x;
                midpoint.y += scratchPos.y;
                midpoint.z += scratchPos.z;
                // Update the upper bounds of the AABB
                if (scratchPos.x > maxPos.x)
                    maxPos.x = scratchPos.x;
                if (scratchPos.y > maxPos.y)
                    maxPos.y = scratchPos.y;
                if (scratchPos.z > maxPos.z)
                    maxPos.z = scratchPos.z;
                // Update the lower bounds of the AABB
                if (scratchPos.x < minPos.x)
                    minPos.x = scratchPos.x;
                if (scratchPos.y < minPos.y)
                    minPos.y = scratchPos.y;
                if (scratchPos.z < minPos.z)
                    minPos.z = scratchPos.z;
                // Calculate the vertex normals in world space
                this.tilingScheme.ellipsoid.geodeticSurfaceNormalForCartographic(scratchCartographic, scratchNrm);
                // Store the vertex normals into the buffer
                normals[i * 3 + posOutOffset] = scratchNrm.x;
                normals[i * 3 + 1 + posOutOffset] = scratchNrm.y;
                normals[i * 3 + 2 + posOutOffset] = scratchNrm.z;
                // Calculate the texture coordinates based on the expected projection
                const uv = this.tilingScheme.projection
                    .project(scratchCartographic, scratchUv)
                    .sub(uvMin)
                    .divide(uvInterval);
                uvs[i * 2 + uvOutOffset] = uv.x;
                uvs[i * 2 + 1 + uvOutOffset] = uv.y;
            }
        }
        // Perform the division of the average to get the output midpoint
        midpoint.divideScalar(vertexCount);
        for (let i = 0; i < vertexCount; i += 1) {
            positions[i * 3] = positions[i * 3] - midpoint.x;
            positions[i * 3 + 1] = positions[i * 3 + 1] - midpoint.y;
            positions[i * 3 + 2] = positions[i * 3 + 2] - midpoint.z;
        }
        // Calculate the radius of a bounding sphere that encompasses all points in the patch
        const radius = maxPos.sub(minPos).length() / 2;
        // Now we need to emit the index buffer, so we produce 3 indices for every triangle we need to emit
        for (let j = 0; j < subdivisionsX - 1; j += 1) {
            const baseOffset = subdivisionsY * j;
            for (let i = 0; i < subdivisionsY - 1; i += 1) {
                const outBase = i * 2 * 3 + subdivisionsY * 6 * j;
                const index0 = outBase + 0;
                const index1 = outBase + 1;
                const index2 = outBase + 2;
                // Even triangle
                indices[index0] = baseOffset + i;
                indices[index1] = baseOffset + i + subdivisionsY;
                indices[index2] = baseOffset + i + 1;
                // Odd triangle
                indices[index0 + 3] = baseOffset + i + subdivisionsY;
                indices[index1 + 3] = baseOffset + i + subdivisionsY + 1;
                indices[index2 + 3] = baseOffset + i + 1;
            }
        }
        return {
            positions,
            normals,
            uvs,
            indices,
            midpoint,
            radius,
        };
    }
    /**
     * Returns the number of vertices that will be emitted by a tessellation of a spheroid given the number of
     * subdivisions along each axis.
     *
     * This allows pre-allocating buffers for the vertex attribute buffers.
     * @param nLat Number of subdivisions along the latitudinal axis
     * @param nLong Number of subdivisions along the longitudinal axis
     */
    static vertexCountFor(nLong, nLat) {
        return nLong * nLat;
    }
    /**
     * Returns the number of vertex indices that will be emitted by a tessellation of a spheroid given the number of
     * subdivisions along each axis.
     *
     * This allows pre-allocating space for the index buffer.
     * @param nLat Number of subdivisions along the latitudinal axis
     * @param nLong Number of subdivisions along the longitudinal axis
     */
    static indexCountFor(nLong, nLat) {
        return this.triangleCountFor(nLong, nLat) * 3;
    }
    /**
     * Returns the number of triangles that will be emitted by a tessellation of a spheroid given the number of
     * subdivisions along each axis.
     *
     * This is used for deducing the number of indices that will be emitted but may have other uses.
     * @param nLat Number of subdivisions along the latitudinal axis
     * @param nLong Number of subdivisions along the longitudinal axis
     */
    static triangleCountFor(nLong, nLat) {
        return (nLong - 1) * 2 * (nLat - 1);
    }
}
